import { maxBy, minBy, partition, uniqWith } from 'es-toolkit';
import { createSelector } from 'reselect';

import {
  BearingEnvelope,
  CalculationError,
  CeilingPlane,
  CeilingTray,
  FeaturePatternTrussContainer,
  RoofContainer,
  RoofPlane,
  TrussEnvelope,
  TrussEnvelopeBevelCut,
} from '@paragon/contract-library';
import {
  convertDegreesToRadians,
  Direction2D,
  Direction3D,
  distanceGreaterThan,
  distanceGreaterThanOrEqual,
  distanceLessThan,
  distanceLessThanOrEqual,
  IsometricEmbedding,
  Line2D,
  Line3D,
  LineSegment2D,
  LineSegment3D,
  minimaFuzzy,
  Plane3D,
  Point2D,
  point2DEquals,
  Point3D,
  Polygon2D,
  Polygon3D,
  Polyhedron,
} from '@paragon/geometry-library';
import {
  circularPairwise,
  min,
  notNullish,
  pairwise,
  ReadonlyRecord,
  Result,
  resultMapOk,
  wrapErr,
  wrapOk,
} from '@paragon/utilities';

import { CalculationErrorFactory } from './calculationErrors';
import { CeilingTrayGeometry } from './ceiling-trays';
import { calculateFeaturePatternContainer3DShapeWithCache } from './featurePatternContainers';
import { getEnvelopeSegment } from './getEnvelopeSegment';
import { PlaneWithCalculatedGeometry } from './roof-plane-types';
import {
  calculateBoundedPlane,
  calculateInferredCeilingPlanes,
  convertPlaneToGeometricPlane,
  getPlaneWithGeometry,
} from './roof-planes';
import { toPolygons } from './solidDataMethods';
import {
  calculateBearingEnvelopeShape,
  calculateTrussEnvelopeShapeWithoutBevels,
  getEnvelopeSegmentFromDefiningSegment,
  getEnvelopeSegmentFromEnvelope,
  TrussEnvelopeBasicShapeProperties,
} from './thickness-calculations';
import { TrussEnvelopeCuts } from './trussEnvelopeCuts';

interface DependenciesWhenUsingRoofContainer {
  roofContainers: ReadonlyRecord<string, RoofContainer>;
}

interface DependenciesWhenUsingFeaturePatternContainer {
  featurePatternContainers: ReadonlyRecord<string, FeaturePatternTrussContainer>;
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>;
}

interface DependenciesWhenUsingRoofPlanes {
  roofPlanes: ReadonlyRecord<string, RoofPlane>;
  ceilingPlanes: ReadonlyRecord<string, CeilingPlane>;
  ceilingTrays: ReadonlyRecord<string, CeilingTray>;
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>;
}

interface DependenciesWhenUsingRoofFaces {
  roofFaces: readonly Polygon3D[];
  ceilingFaces: readonly Polygon3D[];
  inferredCeilingFaces: readonly Polygon3D[];
  getTrussEnvelopeCuts: (envelope: TrussEnvelope) => TrussEnvelopeCuts | null;
  calculateEnvelopeBoundedCeilingPlane: (envelope: TrussEnvelopeBasicShapeProperties) => Polygon3D | null;
}

export type TrussSolidCalculationDependencies =
  | DependenciesWhenUsingRoofContainer
  | DependenciesWhenUsingFeaturePatternContainer
  | DependenciesWhenUsingRoofPlanes
  | DependenciesWhenUsingRoofFaces;

/**
 * @param precomputedData If not provided, these will be calculated from the envelope.
 * @see `calculateTrussSolidWithCache` for memoization.
 */
export function calculateTrussSolid(
  trussEnvelope: TrussEnvelope,
  dependencies: TrussSolidCalculationDependencies,
  precomputedData?: {
    constraints: TrussEnvelopeGeometricConstraints;
    cuts: TrussEnvelopeCuts;
  },
): Result<Polyhedron, CalculationError> {
  const depthDirection = calculateTrussEnvelopeDepthDirection(trussEnvelope);
  if (depthDirection == null) return wrapErr(CalculationErrorFactory.zedgeEnvelope());

  const trussFace = calculateTrussPolygon(trussEnvelope, dependencies, precomputedData);
  if (trussFace?.err) return trussFace;

  return wrapOk(extrudeTrussSolidFromTrussFace(trussFace?.ok, depthDirection, trussEnvelope));
}

/**
 * @param precomputedData If not provided, these will be calculated from the envelope.
 */
export function calculateTrussPolygon(
  trussEnvelope: TrussEnvelope,
  dependencies: TrussSolidCalculationDependencies,
  precomputedData?: {
    constraints: TrussEnvelopeGeometricConstraints;
    cuts: TrussEnvelopeCuts;
  },
): Result<Polygon3D, CalculationError> {
  let constraints = precomputedData?.constraints;
  if (constraints == null) {
    const calculatedConstraints = getGeometricConstraints(trussEnvelope);
    if (calculatedConstraints.err) return calculatedConstraints;
    constraints = calculatedConstraints.ok;
  }
  const cuts = precomputedData?.cuts ?? getTrussEnvelopeCuts(trussEnvelope, dependencies);
  if (cuts == null) return wrapErr(CalculationErrorFactory.missingDependencies());
  try {
    if (trussEnvelope.roofContainerGuid != null) {
      return 'roofContainers' in dependencies
        ? calculateTrussPolygonUsingRoofContainer(trussEnvelope, constraints, cuts, dependencies)
        : wrapErr(CalculationErrorFactory.missingDependencies());
    }

    if (trussEnvelope.featurePatternContainerGuid != null) {
      return 'featurePatternContainers' in dependencies
        ? calculateTrussPolygonUsingFeaturePatternContainer(trussEnvelope, constraints, cuts, dependencies)
        : wrapErr(CalculationErrorFactory.missingDependencies());
    }

    if ('roofPlanes' in dependencies) {
      return calculateTrussPolygonUsingRoofPlanes(trussEnvelope, constraints, cuts, dependencies);
    }

    if ('roofFaces' in dependencies) {
      return calculateTrussPolygonUsingRoofFaces(trussEnvelope, constraints, cuts, dependencies);
    }

    return wrapErr(CalculationErrorFactory.missingDependencies());
  } catch {
    console.error('Truss solid generation encountered an error');
    return wrapErr(CalculationErrorFactory.panicked());
  }
}

function calculateTrussPolygonUsingRoofContainer(
  trussEnvelope: TrussEnvelope,
  constraints: TrussEnvelopeGeometricConstraints,
  cuts: TrussEnvelopeCuts,
  { roofContainers }: DependenciesWhenUsingRoofContainer,
): Result<Polygon3D, CalculationError> {
  if (trussEnvelope.roofContainerGuid == null)
    throw new Error('Expected truss envelope to reference a roof container.');
  const roofContainer = roofContainers[trussEnvelope.roofContainerGuid];
  if (!roofContainer) return wrapErr(CalculationErrorFactory.missingDependencies());

  const roofSolid = calculateRoofContainerSolid(roofContainer);
  return roofSolid
    ? calculateTrussPolygonFromCalculatedPlanes(constraints, roofSolid.faces, cuts)
    : wrapErr(CalculationErrorFactory.nullContainerSolid());
}

function calculateTrussPolygonUsingFeaturePatternContainer(
  trussEnvelope: TrussEnvelope,
  constraints: TrussEnvelopeGeometricConstraints,
  cuts: TrussEnvelopeCuts,
  { featurePatternContainers, bearingEnvelopes }: DependenciesWhenUsingFeaturePatternContainer,
): Result<Polygon3D, CalculationError> {
  if (trussEnvelope.featurePatternContainerGuid == null)
    throw new Error('Expected truss envelope to reference a feature pattern container.');
  const container = featurePatternContainers[trussEnvelope.featurePatternContainerGuid];
  if (!container) return wrapErr(CalculationErrorFactory.missingDependencies());

  const containerSolid = calculateFeaturePatternContainer3DShapeWithCache(container, bearingEnvelopes)?.toPolygons();
  return containerSolid
    ? calculateTrussPolygonFromCalculatedPlanes(constraints, containerSolid.faces, cuts)
    : wrapErr(CalculationErrorFactory.missingDependencies());
}

export function calculateBoundedCeilingPlane(
  trussEnvelope: TrussEnvelopeBasicShapeProperties,
  bearingEnvelopes: readonly BearingEnvelope[],
): Polygon3D | null {
  const envelopePolygon = calculateTrussEnvelopeShapeWithoutBevels(trussEnvelope);
  if (!envelopePolygon) return null;
  const lowestIntersectingBearingHeight = min(
    bearingEnvelopes
      .filter((bearingEnvelope) => {
        const envelopeShape = calculateBearingEnvelopeShape(bearingEnvelope);
        return envelopeShape && Polygon2D.areOverlappingConvex(envelopeShape, envelopePolygon);
      })
      .map(({ top }) => top),
  );
  const ceilingHeight = lowestIntersectingBearingHeight ?? min(Object.values(bearingEnvelopes).map(({ top }) => top));
  if (ceilingHeight == null) return null;

  return envelopePolygon.to3D(ceilingHeight).orientWith({ normalDirection: Direction3D.in });
}

/**
 * All roof/ceiling planes are oriented. This orientation usually corresponds to the outside of the roof, but vertical
 * planes are easy to create with the "wrong" orientation. This method reorients vertical faces when necessary to agree
 * with other faces. For convenience, all `planeFaces` are returned, reoriented as needed.
 */
function orientVerticalPlanes(planeFaces: readonly Polygon3D[], inferredPlaneFaces: readonly Polygon3D[]): Polygon3D[] {
  const [verticalFaces, nonVerticalFaces] = partition(planeFaces, (face) =>
    face.normalDirection.isPerpendicularTo(Direction3D.positiveZ),
  );

  const nonVerticalInferredFaces = inferredPlaneFaces.filter(
    (face) => !face.normalDirection.isPerpendicularTo(Direction3D.positiveZ),
  );

  const edgesOfNonVerticalFaces = nonVerticalFaces.concat(nonVerticalInferredFaces).flatMap(({ edges }) => edges);

  return verticalFaces
    .map((face) =>
      face.reverseIf(
        edgesOfNonVerticalFaces
          .flatMap((edgeOfNonVerticalFace) =>
            face.edges
              .map((edgeOfVerticalFace) =>
                edgeOfNonVerticalFace.overlaps(edgeOfVerticalFace)
                  ? edgeOfNonVerticalFace.direction.dotProduct(edgeOfVerticalFace.direction) > 0
                  : null,
              )
              .filter(notNullish),
          )
          // Co-oriented edges indicate that the vertical plane is mis-oriented. Use a "preponderance of the evidence"
          // check (more wrong than right) to determine whether to reorient.
          .reduce((sum, edgesCooriented) => sum + (edgesCooriented ? -1 : 1), 0) < 0,
      ),
    )
    .concat(nonVerticalFaces);
}

function calculateTrussEnvelopeDepthDirection(trussEnvelope: TrussEnvelope) {
  return point2DEquals(trussEnvelope.leftPoint, trussEnvelope.rightPoint)
    ? null
    : Direction2D.fromPointObjects(trussEnvelope.leftPoint, trussEnvelope.rightPoint)
        .rotateReverseQuarterTurn()
        .to3D()!;
}

function calculateTrussPolygonUsingRoofPlanes(
  trussEnvelope: TrussEnvelope,
  constraints: TrussEnvelopeGeometricConstraints,
  cuts: TrussEnvelopeCuts,
  dependencies: DependenciesWhenUsingRoofPlanes,
): Result<Polygon3D, CalculationError> {
  const { roofPlanes, ceilingPlanes, ceilingTrays, bearingEnvelopes } = dependencies;
  const geometries = getRoofPlaneGeometriesWithCache(
    roofPlanes,
    ceilingPlanes,
    ceilingTrays,
    bearingEnvelopes,
    trussEnvelope.levelGuid,
  );
  return geometries
    ? calculateTrussPolygonUsingRoofFaces(trussEnvelope, constraints, cuts, geometries)
    : wrapErr(CalculationErrorFactory.missingDependencies());
}

function calculateTrussPolygonUsingRoofFaces(
  trussEnvelope: TrussEnvelope,
  constraints: TrussEnvelopeGeometricConstraints,
  cuts: TrussEnvelopeCuts,
  dependencies: DependenciesWhenUsingRoofFaces,
): Result<Polygon3D, CalculationError> {
  const { roofFaces, ceilingFaces, inferredCeilingFaces, calculateEnvelopeBoundedCeilingPlane } = dependencies;

  const envelopeBoundedCeilingPlane = calculateEnvelopeBoundedCeilingPlane(trussEnvelope);

  return calculateTrussPolygonFromCalculatedPlanes(
    constraints,
    [...roofFaces, ...ceilingFaces, ...inferredCeilingFaces, envelopeBoundedCeilingPlane].filter(notNullish),
    cuts,
  );
}

/** Bundles geometric items relevant to multiple parts of truss solid generation. */
interface TrussEnvelopeGeometricConstraints {
  /** The direction from the back of the truss envelope to the front. */
  readonly trussDepthDirection: Direction3D;
  /** The vertical plane defining the front face of the truss envelope. */
  readonly frontPlane: Plane3D;
  /** The vertical plane defining the back face of the truss envelope. */
  readonly backPlane: Plane3D;
  /**
   * An embedding of the back plane such that 3D up is represented by the positive y direction and truss envelope right
   * is represented by the positive x direction.
   */
  readonly backEmbedding: IsometricEmbedding;
  /** The vertical line at the leftmost end of the envelope, including any bevel, as embedded in `backEmbedding`. */
  readonly leftBevelLine: Line2D;
  /** The vertical line at the rightmost end of the envelope, including any bevel, as embedded in `backEmbedding`. */
  readonly rightBevelLine: Line2D;
  /** The vertical line at the leftmost end of the envelope, excluding any bevel, as embedded in `backEmbedding`. */
  readonly leftLine: Line2D;
  /** The vertical line at the rightmost end of the envelope, excluding any bevel, as embedded in `backEmbedding`. */
  readonly rightLine: Line2D;
}

function getGeometricConstraints(
  trussEnvelope: TrussEnvelope,
): Result<TrussEnvelopeGeometricConstraints, CalculationError> {
  // Create front & back face planes(oriented inward)
  const definitionSegment = getEnvelopeSegment(trussEnvelope);
  if (definitionSegment == null) return wrapErr(CalculationErrorFactory.zedgeEnvelope());
  const frontLine = getEnvelopeSegmentFromDefiningSegment(definitionSegment, trussEnvelope, 'Front');
  const backLine = getEnvelopeSegmentFromDefiningSegment(definitionSegment, trussEnvelope, 'Back');
  const trussRightDirection = Direction2D.fromPointObjects(trussEnvelope.leftPoint, trussEnvelope.rightPoint);
  const trussDepthDirection = trussRightDirection.rotateReverseQuarterTurn().to3D()!; // Back-to-front direction

  const frontPlane = Plane3D.fromPerpendicularDirectionAndPoint(
    trussDepthDirection.reverse(),
    frontLine.midpoint.to3D(),
  );
  const backPlane = Plane3D.fromPerpendicularDirectionAndPoint(trussDepthDirection, backLine.midpoint.to3D());

  // Create back embedding (originating from elevation & left line)
  const backEmbedding = new IsometricEmbedding(
    trussRightDirection.to3D()!,
    Direction3D.positiveZ,
    backLine.basePoint.to3D(),
  );

  // Create oriented (counterclockwise) lines at left/right points
  // Adjust inward to account for deepest intrusion of bevel cuts
  const trussThickness = trussEnvelope.thickness * trussEnvelope.numberOfPlies;
  const getBevelCutTrimDistance = (bevelCut: TrussEnvelopeBevelCut | null): number =>
    bevelCut == null
      ? 0
      : trussThickness * Math.tan(convertDegreesToRadians(bevelCut.angle)) * (bevelCut.type === 'Double' ? 0.5 : 1);
  const leftTrimDistance = getBevelCutTrimDistance(trussEnvelope.leftBevelCut);
  const rightTrimDistance = getBevelCutTrimDistance(trussEnvelope.rightBevelCut);

  const leftBevelLine = backEmbedding.lineTo2D(new Line3D(Direction3D.negativeZ, backLine.basePoint.to3D()));
  const rightBevelLine = backEmbedding.lineTo2D(new Line3D(Direction3D.negativeZ, backLine.endPoint.to3D()));
  const leftLine = leftBevelLine.translate(Direction2D.right.toVector(leftTrimDistance));
  const rightLine = rightBevelLine.translate(Direction2D.left.toVector(rightTrimDistance));

  return wrapOk({
    trussDepthDirection,
    frontPlane,
    backPlane,
    backEmbedding,
    leftBevelLine,
    rightBevelLine,
    leftLine,
    rightLine,
  });
}

function getTrussEnvelopeCuts(envelope: TrussEnvelope, dependencies: TrussSolidCalculationDependencies) {
  if (
    'roofPlanes' in dependencies &&
    envelope.roofContainerGuid == null &&
    envelope.featurePatternContainerGuid == null
  ) {
    const { roofPlanes, ceilingPlanes, ceilingTrays, bearingEnvelopes } = dependencies;
    const geometries = getRoofPlaneGeometriesWithCache(
      roofPlanes,
      ceilingPlanes,
      ceilingTrays,
      bearingEnvelopes,
      envelope.levelGuid,
    );
    return geometries.getTrussEnvelopeCuts(envelope) ?? null;
  }

  if (
    'roofFaces' in dependencies &&
    envelope.roofContainerGuid == null &&
    envelope.featurePatternContainerGuid == null
  ) {
    return dependencies.getTrussEnvelopeCuts(envelope);
  }

  if (envelope.roofContainerGuid != null || envelope.featurePatternContainerGuid != null) {
    return TrussEnvelopeCuts.fromTrussEnvelope(
      envelope,
      () => {
        throw new Error('Cannot use roof plane cuts in a roof or feature pattern container.');
      },
      () => {
        throw new Error('Cannot use ceiling plane cuts in a roof or feature pattern container.');
      },
    );
  }

  return null;
}

/**
 * @param roofFaces All polygons that contribute to truss solid shape: roof, ceiling, tray, & container polygons; actual or inferred.
 */
function calculateTrussPolygonFromCalculatedPlanes(
  constraints: TrussEnvelopeGeometricConstraints,
  roofFaces: readonly Polygon3D[],
  trussEnvelopeCuts: TrussEnvelopeCuts,
): Result<Polygon3D, CalculationError> {
  const {
    trussDepthDirection,
    frontPlane,
    backPlane,
    backEmbedding,
    leftBevelLine,
    rightBevelLine,
    leftLine,
    rightLine,
  } = constraints;
  const facePlanes = [frontPlane, backPlane];

  // Create collection of face segments
  //   For each face (outward-oriented 3D polygon)
  //     Intersect face against envelope face planes to get segments
  //     Orient segments counterclockwise
  //     Project segments to back plane and embed
  //     Exclude segments outside of left/right boundary lines
  //     Add segments to collection
  const getFaceSegmentsInPlane = (
    face: Polygon3D,
    plane: Plane3D,
    segmentDirection: Direction3D,
  ): readonly LineSegment3D[] =>
    face.edges
      .filter((segment) => plane.containsSegment(segment))
      .map((segment) => segment.orientWith(segmentDirection));
  const faceSegments = roofFaces
    .flatMap((face) =>
      face.normalDirection.isParallelTo(trussDepthDirection)
        ? []
        : facePlanes
            .concat(trussEnvelopeCuts.planeCuts)
            .reduce(
              (partiallySlicedRoofFaces, slicingPlane) =>
                partiallySlicedRoofFaces.flatMap((partiallySlicedRoofFace) =>
                  partiallySlicedRoofFace.sliceOriented(slicingPlane),
                ),
              [face],
            ),
    )
    .flatMap((face) => {
      // Counterclockwise orientation of segments
      const segmentDirection = trussDepthDirection.crossProductDirection(face.normalDirection)!;

      return getFaceSegmentsInPlane(face, frontPlane, segmentDirection)
        .map((segment) => segment.projectOntoPlane(backPlane)!)
        .concat(getFaceSegmentsInPlane(face, backPlane, segmentDirection))
        .map((segment) => backEmbedding.segmentTo2D(segment));
    })
    // Exclude segments that are coincident with or beyond the side lines
    .map((segment) => {
      if (segment.direction.isParallelTo(Direction2D.up))
        return distanceGreaterThan(segment.midpoint.x, leftLine.basePoint.x) &&
          distanceLessThan(segment.midpoint.x, rightLine.basePoint.x)
          ? segment
          : null;

      return segment
        .splitAtPoint(segment.toLine().intersectWithLine(leftLine)!)
        .flatMap((splitOnce) => splitOnce.splitAtPoint(segment.toLine().intersectWithLine(rightLine)!))
        .filter(
          (splitTwice) =>
            distanceGreaterThan(splitTwice.midpoint.x, leftLine.basePoint.x) &&
            distanceLessThan(splitTwice.midpoint.x, rightLine.basePoint.x),
        )[0];
    })
    .filter(notNullish);

  // Create collection of cut segments
  //   For each plane cut (top/bottom cuts & cap planes) (oriented inward)
  //     Intersect with face that produces larger interior angle
  //       This is the plane that will have the “more interior” intersection line
  //       Results in oriented line
  //     Project intersection line to back plane and embed
  //     Intersect line with left/right lines to produce segment
  //     Add segment to collection
  const cutSegments = trussEnvelopeCuts.planeCuts
    .map((cutPlane) => {
      const intersectingPlane = minBy(facePlanes, (facePlane) => facePlane.angleBetweenNormals(cutPlane))!;
      const cutOrientation3D = frontPlane.halfSpace.direction.crossProductDirection(cutPlane.halfSpace.direction);
      if (cutOrientation3D == null) return null;
      const cutOrientation = backEmbedding.directionTo2D(cutOrientation3D);
      const intersection = backEmbedding.lineTo2D(
        intersectingPlane.intersectWithPlane(cutPlane)!.projectOntoPlane(backPlane)!,
      );
      const leftIntersection = intersection.intersectWithLine(leftLine);
      const rightIntersection = intersection.intersectWithLine(rightLine);
      if (leftIntersection == null || rightIntersection == null) return null;
      return new LineSegment2D(leftIntersection, rightIntersection).orientWith(cutOrientation);
    })
    .filter(notNullish);

  const includeSegmentAfterCuts = (cutSegments: readonly LineSegment2D[]) => {
    const cutSegmentsPreprocessed = cutSegments.map((cutSegment) => {
      const interiorNormal = cutSegment.direction.rotateQuarterTurn();
      return { threshold: cutSegment.midpoint.getCoordinate(interiorNormal), direction: interiorNormal };
    });
    return (segment: LineSegment2D): boolean =>
      cutSegmentsPreprocessed.every(({ threshold, direction }) =>
        segment.endPoints.every((endpoint) => distanceGreaterThanOrEqual(endpoint.getCoordinate(direction), threshold)),
      );
  };

  // Combine collections into one collection of ccw-oriented segments and split all by each other
  const allSegments = uniqWith(
    LineSegment2D.splitAllSegmentsByEachOtherWithoutDeduplication(faceSegments.concat(cutSegments)).filter(
      // Remove any segments that are “outside” segments in cut segment collection
      includeSegmentAfterCuts(cutSegments),
    ),
    (segment1, segment2) => segment1.equals(segment2) && segment1.direction.equals(segment2.direction),
  );

  // Remove dead-end segments
  // FIXME: I (Avery) think it would be better to fail envelopes that have internal dead-end segments, but doing
  // so will result in a large `LayoutTesting` diff with many envelopes now producing null solids. The call above
  // to .DistinctByEquatable(segment => (segment, segment.Direction)) could then be removed too and may help
  // performance a little.
  let cleanedSegments = allSegments;
  let removed = true;
  while (removed) {
    removed = false;
    const allSegmentsCount = cleanedSegments.length;
    cleanedSegments = cleanedSegments.filter((segment) =>
      segment.endPoints.every(
        (point) =>
          point.isOnLine(leftLine) ||
          point.isOnLine(rightLine) ||
          cleanedSegments
            .filter((otherSegment) => !otherSegment.equals(segment))
            .some((otherSegment) => otherSegment.endPoints.some((otherEndpoint) => otherEndpoint.equals(point))),
      ),
    );
    removed = allSegmentsCount !== cleanedSegments.length;
  }

  const { ok: boundaries, err: boundaryError } = findBoundaryPoints(
    cleanedSegments,
    leftLine,
    rightLine,
    backPlane,
    frontPlane,
    backEmbedding,
    trussEnvelopeCuts,
  );
  if (boundaryError != null) return wrapErr(boundaryError);
  const [topPoints, bottomPoints] = [boundaries.topPoints.slice(), boundaries.bottomPoints.slice()];

  // Adjust points to include bevel cuts
  const getBevelPoint = (top: boolean, left: boolean): Point2D => {
    const points = top ? topPoints : bottomPoints;
    const bevelLine = left ? leftBevelLine : rightBevelLine;
    return pairwise(top === left ? points.toReversed() : points).map(([basepoint, endpoint]) =>
      Line2D.fromPoints(basepoint, endpoint).intersectWithLine(bevelLine),
    )[0]!;
  };
  topPoints[0] = getBevelPoint(true, false);
  topPoints[topPoints.length - 1] = getBevelPoint(true, true);
  bottomPoints[0] = getBevelPoint(false, true);
  bottomPoints[bottomPoints.length - 1] = getBevelPoint(false, false);

  // Combine paths, possibly with left/right heel segments
  const backPolygon = Polygon2D.fromVerticesIfValid(
    Polygon2D.removeCollinearPoints(
      circularPairwise(topPoints.concat(bottomPoints))
        .map(([basepoint, endpoint]) => (basepoint.equals(endpoint) ? null : basepoint))
        .filter(notNullish),
    ),
  );
  if (backPolygon == null) return wrapErr(CalculationErrorFactory.nullTrussPolygon());
  return wrapOk(backEmbedding.polygonTo3D(backPolygon));
}

function findBoundaryPoints(
  cleanedSegments: readonly LineSegment2D[],
  leftLine: Line2D,
  rightLine: Line2D,
  backPlane: Plane3D,
  frontPlane: Plane3D,
  backPlaneEmbedding: IsometricEmbedding,
  trussEnvelopeCuts: TrussEnvelopeCuts,
): Result<{ topPoints: readonly Point2D[]; bottomPoints: readonly Point2D[] }, CalculationError> {
  let topPoints: readonly Point2D[] | undefined = undefined;
  let bottomPoints: readonly Point2D[] | undefined = undefined;
  let err: CalculationError | undefined = undefined;
  const calculateBoundary = (top: boolean): Result<readonly Point2D[], CalculationError> => {
    const orderedCuts = top ? trussEnvelopeCuts.orderedTopCuts : trussEnvelopeCuts.orderedBottomCuts;
    if (orderedCuts.length === 0) {
      return findBoundaryFromSegments(cleanedSegments, top, leftLine, rightLine, {
        leftLimit: top
          ? (bottomPoints && bottomPoints[0].y) || null
          : (topPoints && topPoints[topPoints.length - 1].y) || null,
        rightLimit: top
          ? (bottomPoints && bottomPoints[bottomPoints.length - 1].y) || null
          : (topPoints && topPoints[0].y) || null,
      });
    } else {
      return resultMapOk(
        TrussEnvelopeCuts.calculateBoundaryPoints(
          leftLine,
          rightLine,
          backPlane,
          frontPlane,
          backPlaneEmbedding,
          orderedCuts,
          top,
        ),
        (ok) => ok.boundary!,
      );
    }
  };

  // Ordered-cut boundaries have precedence over roof geometry boundaries. If the top and bottom boundaries are
  // the same type, we default to calculating the top boundary first.
  const calculateTopBoundaryFirst =
    trussEnvelopeCuts.orderedTopCuts.length !== 0 || trussEnvelopeCuts.orderedBottomCuts.length === 0;

  if (calculateTopBoundaryFirst) {
    ({ ok: topPoints, err } = calculateBoundary(true));
    if (err != null) return wrapErr(err);
    ({ ok: bottomPoints, err } = calculateBoundary(false));
    if (err != null) return wrapErr(err);
  } else {
    ({ ok: bottomPoints, err } = calculateBoundary(false));
    if (err != null) return wrapErr(err);
    ({ ok: topPoints, err } = calculateBoundary(true));
    if (err != null) return wrapErr(err);
  }

  if (topPoints == null || bottomPoints == null)
    throw new Error('Expected topPoints and bottomPoints to be non-null. This is a bug.');

  // If both the top and bottom boundaries were determined with ordered cuts, we need to verify the boundaries are
  // not misordered with respect to elevation. This is already guaranteed for the other cases via left/rightLimit.
  if (trussEnvelopeCuts.orderedTopCuts.length !== 0 && trussEnvelopeCuts.orderedBottomCuts.length !== 0) {
    const disorientedLeft = distanceGreaterThan(bottomPoints[0].y, topPoints[topPoints.length - 1].y);
    const disorientedRight = distanceGreaterThan(bottomPoints[bottomPoints.length - 1].y, topPoints[0].y);
    if (disorientedLeft || disorientedRight)
      return wrapErr(CalculationErrorFactory.disorientedOrderedCuts(disorientedLeft, disorientedRight));
  }

  return wrapOk({ topPoints, bottomPoints });
}

/**
 * Calculates the boundary formed by segments between the left/right lines.
 * @param cleanedSegments The segments to follow.
 * @param top Whether the boundary forms the top or bottom of the solid.
 * @param leftLine The left boundary of the truss polygon.
 * @param rightLine The right boundary of the truss polygon.
 * @param limits If known, the elevations of the opposite boundaries at each side.
 * @returns The counterclockwise-oriented list of points representing the boundary, or null on failure.
 */
function findBoundaryFromSegments(
  cleanedSegments: readonly LineSegment2D[],
  top: boolean,
  leftLine: Line2D,
  rightLine: Line2D,
  limits: { leftLimit: number | null; rightLimit: number | null },
): Result<Point2D[], CalculationError> {
  const leftCheckpoint = findWalkCheckpoint(cleanedSegments, top, leftLine, limits.leftLimit);
  const rightCheckpoint = findWalkCheckpoint(cleanedSegments, top, rightLine, limits.rightLimit);

  if (leftCheckpoint == null || rightCheckpoint == null)
    return wrapErr(
      CalculationErrorFactory.boundaryCornerFailure(top, leftCheckpoint == null, rightCheckpoint == null, false),
    );

  const [start, end] = top ? [rightCheckpoint, leftCheckpoint] : [leftCheckpoint, rightCheckpoint];

  const boundary = walkSegments(cleanedSegments, top, start, end);
  return boundary == null ? wrapErr(CalculationErrorFactory.boundaryFailure(top, false)) : wrapOk(boundary);
}

function findWalkCheckpoint(
  cleanedSegments: readonly LineSegment2D[],
  top: boolean,
  line: Line2D,
  limit: number | null,
): Point2D | null {
  const extremaBy = top ? minBy : maxBy;
  return (
    extremaBy(
      cleanedSegments
        .filter((segment) => segment.direction.isOrientedWith(top ? Direction2D.left : Direction2D.right))
        .map((segment) => segment.intersectWithLine(line))
        .filter(notNullish)
        .filter(
          (point) =>
            limit == null ||
            (top ? distanceGreaterThanOrEqual(point.y, limit) : distanceLessThanOrEqual(point.y, limit)),
        ),
      (point) => point.y,
    ) ?? null
  );
}

function walkSegments(
  cleanedSegments: readonly LineSegment2D[],
  top: boolean,
  start: Point2D,
  end: Point2D,
): Point2D[] | null {
  let lastDirection = top ? Direction2D.left : Direction2D.right;
  const traversedPoints = [start];

  const done = () =>
    (top ? distanceLessThanOrEqual : distanceGreaterThanOrEqual)(traversedPoints[traversedPoints.length - 1].x, end.x);
  while (!done()) {
    const nextSegment = maxBy(
      cleanedSegments.filter((segment) => segment.basePoint.equals(traversedPoints[traversedPoints.length - 1])),
      (segment) => lastDirection.angleTo(segment.direction),
    );

    if (nextSegment == null) return null;

    // If we looped back to an earlier point, walking has failed
    if (traversedPoints.some((point) => point.equals(nextSegment.endPoint))) return null;

    traversedPoints.push(nextSegment.endPoint);
    lastDirection = nextSegment.direction;
  }

  // Check that the final point is as expected
  if (!traversedPoints[traversedPoints.length - 1].equals(end)) return null;

  return traversedPoints;
}

function extrudeTrussSolidFromTrussFace(
  trussFace: Polygon3D,
  depthDirection: Direction3D,

  sourceEnvelope: TrussEnvelope,
) {
  return trussFace
    .removeCollinearPoints()
    .extrude(depthDirection.toVector(sourceEnvelope.thickness * sourceEnvelope.numberOfPlies));
}

const getRoofPlaneGeometries = (
  roofPlanes: ReadonlyRecord<string, RoofPlane>,
  ceilingPlanes: ReadonlyRecord<string, CeilingPlane>,
  ceilingTrays: ReadonlyRecord<string, CeilingTray>,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
  levelGuid: string | null,
) => {
  const calculatePlanesWithCalculatedGeometry = (planes: ReadonlyRecord<string, RoofPlane | CeilingPlane>) =>
    Object.fromEntries(
      Object.entries(planes).map(([planeGuid, plane]) => {
        const planeWithCalculatedGeometry = getPlaneWithGeometry(plane, bearingEnvelopes);
        return [planeGuid, planeWithCalculatedGeometry] as const;
      }),
    );

  const calculateBoundedPlanes = (
    planesWithCalculatedGeometry: readonly PlaneWithCalculatedGeometry<RoofPlane | CeilingPlane>[],
    allPlanesWithCalculatedGeometry: ReadonlyRecord<
      string,
      PlaneWithCalculatedGeometry<RoofPlane | CeilingPlane> | null
    >,
  ) =>
    planesWithCalculatedGeometry.flatMap(
      (planeWithCalculatedGeometry) =>
        calculateBoundedPlane(planeWithCalculatedGeometry, allPlanesWithCalculatedGeometry).shape,
    );

  const roofPlanesWithCalculatedGeometry = calculatePlanesWithCalculatedGeometry(roofPlanes);
  const ceilingPlanesWithCalculatedGeometry = calculatePlanesWithCalculatedGeometry(ceilingPlanes);

  const facesFromRoofPlanes = calculateBoundedPlanes(
    Object.values(roofPlanesWithCalculatedGeometry)
      .filter(notNullish)
      .filter((plane) => plane.levelGuid === levelGuid),
    roofPlanesWithCalculatedGeometry,
  );
  const facesFromCeilingPlanes = calculateBoundedPlanes(
    Object.values(ceilingPlanesWithCalculatedGeometry)
      .filter(notNullish)
      .filter((plane) => plane.levelGuid === levelGuid),
    ceilingPlanesWithCalculatedGeometry,
  );

  const bearingEnvelopesInLevel = Object.values(bearingEnvelopes).filter(
    (envelope) => envelope.levelGuid === levelGuid,
  );

  const calculateEnvelopeBoundedCeilingPlane = (envelope: TrussEnvelopeBasicShapeProperties) =>
    calculateBoundedCeilingPlane(envelope, bearingEnvelopesInLevel);

  const roofFaces = orientVerticalPlanes(facesFromRoofPlanes, []);

  const inferredCeilingFaces = calculateInferredCeilingPlanes(facesFromCeilingPlanes, bearingEnvelopesInLevel).filter(
    notNullish,
  );

  const orientedCeilingPlaneFaces = orientVerticalPlanes(facesFromCeilingPlanes, inferredCeilingFaces);

  const facesFromCeilingTrays = Object.values(ceilingTrays)
    .map(CeilingTrayGeometry.fromTray)
    .filter(notNullish)
    .flatMap((trayGeometry) => trayGeometry.faces);

  const ceilingFaces = [...orientedCeilingPlaneFaces, ...facesFromCeilingTrays];

  const getTrussEnvelopeCuts = (envelope: Parameters<typeof TrussEnvelopeCuts.fromTrussEnvelope>[0]) =>
    TrussEnvelopeCuts.fromTrussEnvelope(
      envelope,
      (roofPlaneGuid, planeSide) => {
        const roofPlane = roofPlanesWithCalculatedGeometry[roofPlaneGuid];
        return roofPlane ? convertPlaneToGeometricPlane(roofPlane, planeSide) : null;
      },
      (ceilingPlaneGuid) => {
        const ceilingPlane = ceilingPlanesWithCalculatedGeometry[ceilingPlaneGuid];
        return ceilingPlane ? convertPlaneToGeometricPlane(ceilingPlane, 'Bottom') : null;
      },
    );

  return { roofFaces, ceilingFaces, inferredCeilingFaces, getTrussEnvelopeCuts, calculateEnvelopeBoundedCeilingPlane };
};

const getTrussEnvelopeGeometricConstraintsWithCache = createSelector(
  (envelope: TrussEnvelope) => envelope,
  getGeometricConstraints,
);

const getTrussEnvelopeCutsWithCache = createSelector(
  (envelope: TrussEnvelope) => envelope,
  (envelope: TrussEnvelope, dependencies: TrussSolidCalculationDependencies) => dependencies,
  getTrussEnvelopeCuts,
);

export const getTrussEnvelopeCutFacetsWithCache = createSelector(
  (envelope: TrussEnvelope) => envelope,
  getTrussEnvelopeGeometricConstraintsWithCache,
  (envelope: TrussEnvelope, dependencies: TrussSolidCalculationDependencies) => dependencies,
  getTrussEnvelopeCutsWithCache,
  (envelope, constraints, dependencies, cuts) => {
    if (constraints.err || cuts == null) return null;

    const { leftLine, rightLine, backPlane, frontPlane, backEmbedding } = constraints.ok;
    const topCutFacets =
      cuts.orderedTopCuts.length === 0
        ? []
        : (TrussEnvelopeCuts.calculateBoundaryPoints(
            leftLine,
            rightLine,
            backPlane,
            frontPlane,
            backEmbedding,
            cuts.orderedTopCuts,
            true,
            { allowPartial: true },
          ).ok?.cutFacets ?? []);
    const bottomCutFacets =
      cuts.orderedBottomCuts.length === 0
        ? []
        : (TrussEnvelopeCuts.calculateBoundaryPoints(
            leftLine,
            rightLine,
            backPlane,
            frontPlane,
            backEmbedding,
            cuts.orderedBottomCuts,
            false,
            { allowPartial: true },
          ).ok?.cutFacets ?? []);
    return { topCutFacets, bottomCutFacets };
  },
);

export const getRoofPlaneGeometriesWithCache = createSelector(
  (roofPlanes: ReadonlyRecord<string, RoofPlane>) => roofPlanes,
  (roofPlanes: ReadonlyRecord<string, RoofPlane>, ceilingPlanes: ReadonlyRecord<string, CeilingPlane>) => ceilingPlanes,
  (
    roofPlanes: ReadonlyRecord<string, RoofPlane>,
    ceilingPlanes: ReadonlyRecord<string, CeilingPlane>,
    ceilingTrays: ReadonlyRecord<string, CeilingTray>,
  ) => ceilingTrays,
  (
    roofPlanes: ReadonlyRecord<string, RoofPlane>,
    ceilingPlanes: ReadonlyRecord<string, CeilingPlane>,
    ceilingTrays: ReadonlyRecord<string, CeilingTray>,
    bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
  ) => bearingEnvelopes,
  (
    roofPlanes: ReadonlyRecord<string, RoofPlane>,
    ceilingPlanes: ReadonlyRecord<string, CeilingPlane>,
    ceilingTrays: ReadonlyRecord<string, CeilingTray>,
    bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
    levelGuid: string | null,
  ) => levelGuid,
  getRoofPlaneGeometries,
);

export const calculateTrussSolidWithCache = createSelector(
  (envelope: TrussEnvelope) => envelope,
  getTrussEnvelopeGeometricConstraintsWithCache,
  (envelope: TrussEnvelope, dependencies: TrussSolidCalculationDependencies) => dependencies,
  getTrussEnvelopeCutsWithCache,
  (envelope, constraints, dependencies, cuts): Result<Polyhedron, CalculationError> => {
    if (constraints.err) return constraints;
    if (cuts == null) return wrapErr(CalculationErrorFactory.missingDependencies());
    return calculateTrussSolid(envelope, dependencies, { constraints: constraints.ok, cuts });
  },
);

export const calculateRoofContainerSolid = createSelector(
  (roofContainer: RoofContainer) => roofContainer.solidData,
  (solidData) => (solidData ? toPolygons(solidData) : null),
);

export type RoofPlaneSide = 'Top' | 'Bottom';

export interface TrussExtents {
  readonly backLeftPoint: Point3D;
  readonly backRightPoint: Point3D;
  readonly leftOverhangDistance: number;
  readonly rightOverhangDistance: number;
}

export function calculateTrussExtents(trussEnvelope: TrussEnvelope): TrussExtents | null {
  const backSegment = getEnvelopeSegmentFromEnvelope(trussEnvelope, 'Back');
  return (
    backSegment && {
      backLeftPoint: backSegment.basePoint.to3D(trussEnvelope.elevation),
      backRightPoint: backSegment.endPoint.to3D(trussEnvelope.elevation),
      leftOverhangDistance: trussEnvelope.leftOverhang,
      rightOverhangDistance: trussEnvelope.rightOverhang,
    }
  );
}

/**
 * Projects a 3D polygon representing a truss face to its 2D profile in truss coordinates.
 */
export function convertTrussEnvelopePolygonTo2D(polygon: Polygon3D, trussEnvelope: TrussEnvelope) {
  const backSidePoint2D = Point2D.fromObject(trussEnvelope.leftPoint);
  const backSidePoint = minBy(
    minimaFuzzy(polygon.vertices, (vertex) => backSidePoint2D.distanceTo(vertex.projectTo2D())),
    (vertex) => vertex.z,
  )!;

  const envelopeDirection = getEnvelopeSegmentFromEnvelope(trussEnvelope, 'Front')!.direction.to3D()!;
  const embedding = new IsometricEmbedding(envelopeDirection, Direction3D.positiveZ, backSidePoint);

  return embedding.polygonTo2D(polygon);
}

export function calculateTrussPolygon2D(
  trussEnvelope: TrussEnvelope,
  dependencies: TrussSolidCalculationDependencies,
): Polygon2D | null {
  const polygon3D = calculateTrussPolygon(trussEnvelope, dependencies).ok;
  return polygon3D ? convertTrussEnvelopePolygonTo2D(polygon3D, trussEnvelope) : null;
}
